import { createContext, useEffect, useState, ReactNode } from "react";
import Firebase from "firebase/compat/app";
import {
  Firestore,
  doc,
  getDoc,
} from "firebase/firestore";
import { firebase, db } from "../lips/firebase_initialization";

/* ================= TYPES ================= */

interface ValueProps {
  firebase: Firebase.app.App;
  db: Firestore;
  data: any;
  loadingGettingUserData: boolean;
  error: string | null;
  refresh: (() => Promise<void>) | null;
}

/* ================= CONTEXT ================= */

export const FirebaseContext = createContext<ValueProps>({
  firebase,
  db,
  data: null,
  loadingGettingUserData: true,
  error: null,
  refresh: null,
});

/* ================= PROVIDER ================= */

export function FirebaseProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<any>(null);
  const [loadingGettingUserData, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    const docId = localStorage.getItem("docId");

    if (!docId) {
      setError("No session found");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const ref = doc(db, "bank-application-data", docId);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        setError("Account not found");
        setData(null);
      } else {
        setData({ id: snap.id, ...snap.data() });
      }
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <FirebaseContext.Provider
      value={{
        firebase,
        db,
        data,
        loadingGettingUserData,
        error,
        refresh: fetchData,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
}
